import {
  SalvBanner,
  SalvBannerType,
  SalvBannerAccent,
  SalvButton,
  SalvFieldHeader,
  SalvTable,
  SalvTextField,
  Size,
} from '@salvapp/ui-components';
import { faPlus } from '@fortawesome/pro-solid-svg-icons';
import React, { useMemo } from 'react';
import { createColumnHelper } from '@tanstack/react-table';
import { FormikErrors } from 'formik';
import { FormikValues } from './EntitySchemaField';

interface Props {
  enumOptions: string[];
  setEnumOptions: (values: string[]) => void;
  size: Extract<Size, Size.SMALL | Size.EXTRA_SMALL>;
  errors: FormikErrors<FormikValues>;
  className?: string;
}

export const SchemaEnumOptions = ({ enumOptions, setEnumOptions, size, errors, className }: Props) => {
  const columnHelper = createColumnHelper<{ option: string }>();
  const columns = useMemo(
    () => [
      columnHelper.accessor('option', {
        header: 'Option name',
        cell: ({ row, getValue, table }) => {
          const updateRow = (value: string) => {
            const newValues = [...table.getRowModel().rows.map((row) => row.original.option)];
            newValues[row.index] = value;
            setEnumOptions(newValues);
          };
          return (
            <SalvTextField
              ariaLabel={`Option ${row.index + 1}`}
              defaultValue={getValue()}
              placeholder={`Option ${row.index + 1}`}
              size={size}
              onChange={(value) => updateRow(value)}
            />
          );
        },
      }),
      columnHelper.display({
        id: 'remove',
        cell: ({ row, table }) => {
          const removeRow = () => {
            const newValues = [...table.getRowModel().rows.map((row) => row.original.option)];
            newValues.splice(row.index, 1);
            setEnumOptions(newValues);
          };
          return (
            <SalvButton
              label={'Remove'}
              ariaLabel={`Remove field ${row.index + 1}`}
              variant='danger-outline'
              size={size}
              disabled={enumOptions.length <= 1}
              onClick={removeRow}
            />
          );
        },
        size: 0,
      }),
    ],
    [enumOptions.length]
  );

  return (
    <div className={className}>
      {errors.enumOptions && (
        <SalvBanner
          accent={SalvBannerAccent.STRONG}
          message={errors.enumOptions}
          type={SalvBannerType.DANGER}
          className={'m-b-8'}
        />
      )}
      <SalvFieldHeader
        label='Define field options'
        description='These options will be available when selecting a value for this field'
        size={size === Size.SMALL ? Size.MEDIUM : Size.SMALL}
      />
      <SalvTable
        columns={columns}
        data={enumOptions.map((option) => {
          return { option };
        })}
        striped
        size={Size.SMALL}
      />
      <SalvButton
        icon={faPlus}
        label={'Add option'}
        variant='secondary'
        size={size}
        className={'m-t-8'}
        onClick={() => {
          setEnumOptions([...enumOptions!, '']);
        }}
      />
    </div>
  );
};
